import useConfig from '@plug-core/hooks/useConfig'
import useToken from '@plug-core/hooks/useToken'
import { RequestTransferParams } from '@plug-core/interfaces/transfers'
import { arrayBufferToBase64 } from '@plug-core/utils/arrayBuffer'
import { formatMoney, formatNumber } from '@plug-core/utils/formatNumber'
import { shortAddress } from '@plug-core/utils/shortAddress'
import { e8sToDecimals } from '@plug-core/utils/validationHelper'
import { Avatar, Heading, Surface, Tabs, TabsContent, TabsList, TabsTrigger, Text } from '@ui'
import React, { FC, useState } from 'react'
import { HiChevronDoubleRight } from 'react-icons/hi2'

interface Props {
  transferParams: RequestTransferParams
  tokenCanisterId: string
}

const Transfer: FC<Props> = ({ transferParams, tokenCanisterId }) => {
  const [currentTab, setCurrentTab] = useState(0)

  const { tokenPricesWithChange } = useConfig()
  const { id, decimals, logo, symbol, fee } = useToken({ address: tokenCanisterId })

  const tokenPrice = id ? tokenPricesWithChange[id] : undefined

  let usdAmount
  if (typeof tokenPrice?.price === 'number') {
    usdAmount = transferParams.amount * 10 ** -(decimals || 8) * tokenPrice.price
  }

  const renderArrowView = () => {
    return (
      <span className="absolute grid place-items-center rounded-full bg-bg p-2 text-[30px] text-accent">
        <HiChevronDoubleRight aria-hidden="true" />
      </span>
    )
  }

  const renderPropertyRow = (title: string, value: string | number) => (
    <div className="flex flex-col gap-2">
      <Text variant="body-s" tone="fg-2">
        {title}
      </Text>
      <div className="min-h-5 rounded-sm bg-surface px-4 py-2">
        <Text variant="mono" tone="fg" breakAll>
          {value}
        </Text>
      </div>
    </div>
  )

  return (
    <Tabs
      value={String(currentTab)}
      onValueChange={(newValue) => setCurrentTab(Number(newValue))}
      className="flex-1"
    >
      <TabsList className="self-stretch">
        <TabsTrigger value="0" className="flex-1">
          Info
        </TabsTrigger>
        <TabsTrigger value="1" className="flex-1">
          Parameters
        </TabsTrigger>
      </TabsList>
      <TabsContent value="0" className="pb-4">
        <div className="flex flex-1 flex-col justify-between">
          <div className="relative mb-7 flex items-center justify-center">
            <Surface className="ml-2 flex flex-1 flex-col items-center gap-3">
              <Avatar src={logo} fallback={symbol} />
              <Text variant="caption" align="center">
                {symbol}
              </Text>
            </Surface>
            {renderArrowView()}
            <Surface className="mx-2 flex flex-1 flex-col items-center gap-3">
              <Avatar emoji="😇" />
              <Text variant="mono" align="center" className="text-[12px]">
                {shortAddress(transferParams.to)}
              </Text>
            </Surface>
          </div>

          <div className="flex flex-col gap-1 px-4">
            <div className="flex items-start justify-between gap-4 py-1.5">
              <Text weight="medium">Transfer amount</Text>
              <div className="flex flex-col items-end gap-1 text-right">
                <Heading level={3} tabular className="text-[23px] text-info">
                  {formatNumber(e8sToDecimals(transferParams.amount, decimals || 8), symbol)}
                </Heading>
                <Text variant="body-s" tone="fg-2" tabular>
                  {formatMoney(usdAmount)}
                </Text>
              </div>
            </div>
            <div className="flex items-start justify-between gap-4 py-1.5">
              <Text weight="medium">Fee</Text>
              <Text variant="body-s" tone="fg-2" tabular align="end">
                {formatNumber(e8sToDecimals(fee || 0, decimals || 8), symbol)}
              </Text>
            </div>
          </div>
        </div>
      </TabsContent>
      <TabsContent value="1" className="pb-4">
        <div className="flex flex-col gap-2 px-4">
          {renderPropertyRow('Token Canister ID', tokenCanisterId)}
          {renderPropertyRow('Receiver', transferParams.to)}
          {transferParams.opts?.memo && renderPropertyRow('Memo', transferParams.opts?.memo)}
          {transferParams.subaccount &&
            renderPropertyRow('Subaccount', arrayBufferToBase64(transferParams.subaccount))}
        </div>
      </TabsContent>
    </Tabs>
  )
}

export default Transfer
